import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useStripe as useStripeContext } from '../contexts/StripeContext';
import { confirmPayment } from '../lib/credits';

interface StripeCheckoutProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const StripeCheckout: React.FC<StripeCheckoutProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { clientSecret, setClientSecret } = useStripeContext();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/credit-purchase-success`,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message || 'Payment failed');
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        // Payment succeeded - confirm payment and send email
        try {
          await confirmPayment(paymentIntent.id);
          onSuccess?.();
          setClientSecret(null);
        } catch (confirmError) {
          console.error('Error confirming payment:', confirmError);
          setError('Payment confirmed but there was an error processing your credits');
          // Still call onSuccess since payment was successful
          onSuccess?.();
          setClientSecret(null);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Payment error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setClientSecret(null);
    onCancel?.();
  };

  // Don't render PaymentElement until we have clientSecret
  if (!clientSecret) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto' }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
            {t('credits.checkout.title')}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
            {t('credits.checkout.subtitle')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <PaymentElement
                options={{
                  layout: 'tabs',
                }}
              />
            </Box>

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isProcessing}
                fullWidth
              >
                {t('common.cancel')}
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                disabled={!stripe || !elements || isProcessing}
                startIcon={isProcessing ? <CircularProgress size={20} /> : null}
                fullWidth
                sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  },
                }}
              >
                {isProcessing ? t('credits.checkout.processing') : t('credits.checkout.payNow')}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
